import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { makeForm } from "@/lib/upload";
import { CertidaoPanel } from "@/components/CertidaoPanel";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, AlertCircle, AlertTriangle, Eye, RefreshCw, History, Check, Ban } from "lucide-react";
import { MenuOrSheet } from "@/components/ActionSheet";
import { useAuth } from "@/context/AuthContext";
import { niceName } from "@/lib/format";
import { buildRows } from "@/components/AiPanel";
import { DateInput } from "@/components/DateInput";
import { SignDateDialog } from "@/components/SignDateDialog";
import { RemindDialog } from "@/components/RemindDialog";
import { DocViewer } from "@/components/DocViewer";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TONE, dm, ownerOf, plainName, isNa } from "@/lib/docReasons";
import { Glossed } from "@/components/Glossary";
import { useFocusFlash } from "@/lib/useFocusFlash";
import { PairConflict, AreaCompare } from "@/components/PairConflict";

const btnBase = "h-12 md:h-10 whitespace-normal leading-tight text-center inline-flex items-center justify-center gap-2 px-3 md:px-4 rounded-[14px] text-[14px] md:text-sm font-semibold transition-colors disabled:opacity-50 flex-1 md:flex-none";
const outline = `${btnBase} border border-slate-300 bg-white text-slate-800 hover:bg-slate-50`;
const filled = `${btnBase} navy-bg text-white hover:brightness-110`;
const small = "h-9 inline-flex items-center gap-1.5 px-3 rounded-[12px] border border-slate-300 bg-white text-[13px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50";

export const canDecideOn = (user, p) => user?.role === "gestor" || (user?.role === "consultor" && p.consultant_id === user.id);
const HELPER_LABEL = {
  upload: "Carregar outro", date: "Indicar data de emissão", signdate: "Indicar data de assinatura", reread: "Voltar a ler",
  remove: "Retirar este ficheiro", keep: "Manter os dois", changeType: "É outro documento",
};
const HELPERS = new Set(Object.keys(HELPER_LABEL));
const REVIEW_CODES = new Set(["ai_review", "confirm", "ai_error", "file_type_check"]);

// Um só modo por documento: decide o botão principal.
export function modeOf(doc, reason) {
  if (isNa(doc)) return "na";
  if (!doc.file_id) return "missing";
  if (reason.tone === "green") return "ok";
  return REVIEW_CODES.has(reason.code) && reason.tone !== "red" ? "review" : "resolve";
}

const FIELD_PREFIX = {
  data_emissao: "emitido a", data_validade: "válido até", numero_documento: "N.º", numero_licenca: "N.º", numero_anuncio: "N.º",
  numero_descricao: "Descrição", artigo_matricial: "Artigo", fracao: "fração", data_licenca: "de", data_pagamento: "pago a",
  data_assinatura: "assinado a", data_ficha: "de", data_anuncio: "de", nipc: "NIPC", _terreno: "terreno", _construcao: "construção",
};
const bare = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\b(lda|sa|unipessoal)\b/g, "").replace(/[^a-z0-9]/g, "");
const SHOUT = /^[^a-zà-ÿ]*[A-ZÀ-Ý]{3}[^a-zà-ÿ]*$/;

function fmtField(f, p) {
  let v = /^\d{4}-\d{2}-\d{2}/.test(String(f.value)) ? dm(f.value) : String(f.value).replace(/\s*\([^)]*\)/g, "").trim();
  if (f.key === "numero_descricao") v = v.split("/")[0];
  if (SHOUT.test(v)) {
    const party = [...(p.sellers || []), ...(p.buyers || [])].find((x) => x.name && bare(x.name) === bare(v));
    v = party ? party.name : niceName(v);
  }
  return FIELD_PREFIX[f.key] ? `${FIELD_PREFIX[f.key]} ${v}` : v;
}

// No máximo 3 elementos curtos e inteiros; o resto fica no «Rever».
function compactData(doc, p) {
  if (!doc.ai || doc.ai.error) return [];
  return buildRows(doc.ai).key
    .map((r) => r.fields.find((f) => !(f.key === "data_emissao" && f.confidence && f.confidence !== "alto")))
    .filter(Boolean).map((f) => fmtField(f, p)).filter((t) => t && t.length <= 40).slice(0, 3).map((t) => t.replace(/(\d)\s*m2\b/g, "$1 m²"));
}

function AccessCode({ doc, demo }) {
  const fv = (k) => doc.ai?.fields?.find((f) => f.key === k && f.value)?.value;
  if (doc.key === "caderneta_predial" && fv("codigo_validacao")) {
    return (
      <p className="text-sm text-slate-600 mt-1" data-testid="doc-validation-code-caderneta_predial">
        Código de validação <span className="font-mono font-semibold">{fv("codigo_validacao")}</span>{fv("nif_emissor") && <> · NIF <span className="font-mono">{fv("nif_emissor")}</span></>}{!demo && <> ·{" "}
        <a href="https://www.portaldasfinancas.gov.pt/pt/external/validarDocs/validarDocumentos.action" target="_blank" rel="noreferrer" className="underline font-semibold navy-text" data-testid="doc-validation-link-caderneta_predial">Validar no Portal das Finanças</a></>}
      </p>
    );
  }
  if (!doc.key.startsWith("certidao_comercial_") || !doc.ai?.fields) return null;
  const code = doc.ai.fields.find((f) => ["codigo_acesso", "codigo_validacao"].includes(f.key) && f.value)?.value;
  if (!code) return null;
  const url = `https://registo.justica.gov.pt/Empresas/Consultar-Certidao-Permanente/Iniciar?codcertidao=${encodeURIComponent(code)}`;
  return (
    <p className="text-sm text-slate-600 mt-1" data-testid={`doc-access-code-${doc.key}`}>
      Código de acesso <span className="font-mono font-semibold">{code}</span> ·{" "}
      <a href={url} target="_blank" rel="noreferrer" className="underline font-semibold navy-text" data-testid={`doc-access-link-${doc.key}`}>Confirmar no site oficial</a>
    </p>
  );
}

function useDocActions(pid, doc, onChange) {
  const [busy, setBusy] = useState(null);
  const run = async (k, fn, ok) => {
    setBusy(k);
    try { await fn(); if (ok) toast.success(ok); onChange(); return true; }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Não foi possível concluir"); return false; }
    finally { setBusy(null); }
  };
  const base = `/processes/${pid}/documents/${doc.key}`;
  return {
    busy,
    upload: (file) => run("upload", async () => api.post(`${base}/upload`, await makeForm([["file", file]])), "Ficheiro carregado e lido"),
    reread: () => run("reread", () => api.post(`${base}/reread`), "Documento lido de novo"),
    issue: (v) => run("date", () => api.put(base, { issue_date: v }), "Data de emissão guardada"),
    remove: () => run("remove", () => api.post(`${base}/remove-file`), "Ficheiro retirado"),
    keep: () => run("keep", () => api.post(`${base}/keep-file`), "Ficheiros mantidos"),
    approve: () => run("approve", () => (doc.ai ? api.post(`${base}/confirm-ai`) : api.post(`${base}/resolve`, { note: "Aprovado após revisão" })), "Documento aprovado"),
    again: (reason) => run("again", () => api.post(`${base}/request-again`, { reason }), "Documento pedido de novo"),
    resolve: (note) => run("resolve", () => api.post(`${base}/resolve`, { note }), doc.file_id ? "Aceite com nota" : "Marcado como não necessário"),
    move: (to) => run("move", async () => { await api.post(`/processes/${pid}/documents/${to}/attach-file`, { file_id: doc.file_id }); await api.post(`${base}/remove-file`); }, "Tipo alterado"),
  };
}

function Inline({ kind, doc, p, act, close }) {
  const [note, setNote] = useState("");
  if (kind === "date") {
    return <div className="flex items-center gap-2 mt-3"><span className="text-sm text-slate-500">Data de emissão</span>
      <DateInput autoFocus onChange={(e) => { if (e.target.value) { act.issue(e.target.value); close(); } }} className="h-9 w-40 text-sm" data-testid={`doc-issue-${doc.key}`} />
      <button onClick={close} className="text-sm text-slate-500">cancelar</button></div>;
  }
  if (kind === "type") {
    const opts = p.documents.filter((d) => d.key !== doc.key && d.status !== "nao_aplica");
    return <div className="flex items-center gap-2 mt-3"><span className="text-sm text-slate-500">Este ficheiro é</span>
      <Select onValueChange={(v) => { act.move(v); close(); }}><SelectTrigger className="h-9 w-64 text-sm" data-testid={`doc-type-${doc.key}`}><SelectValue placeholder="Escolher documento" /></SelectTrigger>
        <SelectContent>{opts.map((d) => <SelectItem key={d.key} value={d.key}>{d.name} · {ownerOf(d, p).label}</SelectItem>)}</SelectContent></Select>
      <button onClick={close} className="text-sm text-slate-500">cancelar</button></div>;
  }
  const again = kind === "again";
  const ph = again ? "Porquê? (opcional, ex.: falta o verso)" : doc.file_id ? "Porque se avança assim? (ex.: o notário aceita o comprovativo de renovação)" : "Porque não é preciso? (ex.: o imóvel não tem condomínio)";
  const save = async () => { if (await (again ? act.again(note.trim()) : act.resolve(note))) close(true); };
  return <div className="flex flex-wrap items-center gap-2 mt-3" data-testid={`doc-inline-${kind}-${doc.key}`}>
    <Input autoFocus value={note} onChange={(e) => setNote(e.target.value)} maxLength={again ? 120 : 140} placeholder={ph} className="h-12 md:h-9 text-sm w-full basis-full md:basis-auto md:flex-1 md:min-w-[240px]" data-testid={`doc-${again ? "again" : "resolve"}-note-${doc.key}`} />
    <button onClick={save} disabled={(!again && !note.trim()) || !!act.busy} className={filled} data-testid={`doc-${again ? "again" : "resolve"}-save-${doc.key}`}>{act.busy && <Loader2 size={14} className="animate-spin" />}{again ? "Pedir de novo" : "Guardar"}</button>
    <button onClick={() => close()} className="text-sm text-slate-500">cancelar</button></div>;
}

function MoreMenu({ doc, act, ro, onHistory, notNeeded }) {
  const trigger = <button className="h-12 md:h-10 w-12 md:w-10 shrink-0 inline-flex items-center justify-center rounded-[14px] border border-slate-300 bg-white hover:bg-slate-50 ml-auto" data-testid={`doc-more-${doc.key}`} aria-label="Mais ações"><MoreHorizontal size={16} /></button>;
  const items = [
    doc.file_id && !ro && { label: "Voltar a ler", hint: "A leitura automática lê o ficheiro outra vez", icon: RefreshCw, onClick: act.reread, testid: `doc-menu-reread-${doc.key}` },
    { label: "Ver histórico", icon: History, onClick: () => onHistory(doc), testid: `doc-menu-history-${doc.key}` },
    notNeeded && { label: "Não é preciso neste processo", hint: "Fica registado com uma nota", icon: Ban, onClick: notNeeded, testid: `doc-not-needed-${doc.key}` },
  ];
  return <MenuOrSheet trigger={trigger} items={items} title={plainName(doc)} testid={`doc-menu-${doc.key}`} />;
}

function AlertBox({ reason, doc, pid, ro, onChange, onPaused }) {
  const paused = doc.paused_n || 0;
  const warn = ["red", "amber"].includes(reason.tone) && !!reason.text;
  if (reason.code === "missing" && !reason.text.replace("Documento ainda não entregue.", "").trim()) return null;
  const red = reason.tone === "red";
  const Icon = red ? AlertCircle : AlertTriangle;
  const more = reason.more > 0 ? ` Há mais ${reason.more} ${reason.more === 1 ? "aviso" : "avisos"} em «Resolver».` : "";
  if (reason.tone === "green") return null;
  if (!warn) return <p className="text-[15px] text-slate-700 mt-1" data-testid={`doc-reason-${doc.key}`}><Glossed text={reason.text} />{more}</p>;
  return (
    <>
      <div className={`mt-2 rounded-[14px] px-3.5 py-3 ${red ? "bg-red-50" : "bg-amber-50"}`} data-testid={`doc-alert-${doc.key}`}>
        {reason.title && <div className={`flex items-start gap-1.5 text-[15px] font-semibold ${red ? "text-[#B91C1C]" : "text-[#B45309]"}`} data-testid={`doc-alert-title-${doc.key}`}><Icon size={16} className="shrink-0 mt-0.5" /><Glossed text={reason.title} /></div>}
        <p className={`text-[15px] text-slate-700 line-clamp-2 md:line-clamp-none ${reason.title ? "mt-0.5" : ""}`} data-testid={`doc-reason-${doc.key}`}>{!reason.title && <Icon size={15} className={`inline mr-1 -mt-0.5 ${red ? "text-[#B91C1C]" : "text-[#B45309]"}`} />}<Glossed text={reason.text} /></p>
        {reason.compare && <AreaCompare items={reason.compare} />}
      </div>
      {reason.code === "check_pair" && <PairConflict pid={pid} table={reason.table} ro={ro} onChange={onChange} />}
      {more && <p className="text-sm text-slate-500 mt-1.5">{more.trim()}</p>}
      {paused > 0 && <button type="button" onClick={onPaused} className="text-sm text-slate-500 mt-1.5 hover:underline" data-testid={`doc-paused-${doc.key}`}>{paused} {paused === 1 ? "aviso em pausa" : "avisos em pausa"} · Ver</button>}
    </>
  );
}

function approvedLine(doc) {
  const r = doc.resolution;
  if (r) return [`Aceite com nota por ${niceName(r.by)}`, r.at, r.note];
  if (doc.ai_auto) return ["Verificado automaticamente", doc.status_at];
  if (doc.ai_confirmed_by) return [`Aprovado por ${niceName(doc.ai_confirmed_by)}`, doc.ai_confirmed_at];
  return [doc.status_by ? `Aprovado por ${niceName(doc.status_by)}` : "Aprovado", doc.status_at];
}

function Approved({ doc }) {
  const [who, at, note] = approvedLine(doc);
  return (
    <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#15803D] mr-auto" data-testid={`doc-approved-${doc.key}`}>
      <Check size={16} strokeWidth={2.75} /> {[who, at && dm(at)].filter(Boolean).join(" · ")}{note && <span className="font-normal text-slate-500">· {note}</span>}
    </span>
  );
}

// Dentro do Rever/Resolver: as ajudas do aviso e as 3 decisões finais.
function Decisions({ ctx, reason, mode }) {
  const { doc, act, setInline, decide, fileRef } = ctx;
  const helpers = [reason.primary, reason.secondary].filter((k) => HELPERS.has(k));
  const fns = { upload: () => fileRef.current?.click(), date: () => setInline("date"), signdate: () => setInline("sign"), reread: act.reread, remove: act.remove, keep: act.keep, changeType: () => setInline("type") };
  const canApprove = mode === "review" || (doc.ai && !doc.ai_confirmed && !doc.ai?.error);
  return (
    <div className="mt-3 rounded-[14px] border border-slate-200 bg-slate-50/70 p-3" data-testid={`doc-decisions-${doc.key}`}>
      {mode === "resolve" && helpers.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`doc-helpers-${doc.key}`}>
          {helpers.map((k) => <button key={k} onClick={fns[k]} disabled={!!act.busy} className={small} data-testid={`doc-helper-${k}-${doc.key}`}>
            {act.busy === k && <Loader2 size={13} className="animate-spin" />}{HELPER_LABEL[k]}</button>)}
        </div>
      )}
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Decisão</div>
      <div className="flex flex-wrap gap-2">
        {canApprove && <button onClick={async () => { if (await act.approve()) ctx.close(); }} disabled={!!act.busy} className={filled} data-testid={`doc-approve-${doc.key}`}>{act.busy === "approve" ? <Loader2 size={14} className="animate-spin" /> : <Check size={15} />}Aprovar</button>}
        <button onClick={() => setInline("again")} disabled={!!act.busy} className={outline} data-testid={`doc-again-${doc.key}`}>Pedir de novo</button>
        {mode === "resolve" && decide && <button onClick={() => setInline("resolve")} disabled={!!act.busy} className={outline} data-testid={`doc-accept-note-${doc.key}`}>Aceitar com nota</button>}
      </div>
    </div>
  );
}

const PRIMARY = { review: "Rever", resolve: "Resolver" };

function Actions({ ctx, mode }) {
  const { doc, act, ro, decide, setInline, fileRef, setViewer, show, toggle, onHistory } = ctx;
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3 w-full md:w-auto">
      {mode === "ok" && <Approved doc={doc} />}
      {!ro && PRIMARY[mode] && <button onClick={toggle} className={filled} data-testid={`doc-primary-${doc.key}`} aria-expanded={show}>{show ? "Fechar" : PRIMARY[mode]}</button>}
      {!ro && mode === "missing" && <button onClick={() => fileRef.current?.click()} disabled={!!act.busy} className={filled} data-testid={`doc-primary-${doc.key}`}>{act.busy === "upload" && <Loader2 size={14} className="animate-spin" />}Carregar</button>}
      {!ro && mode === "na" && <button onClick={() => setInline("again")} disabled={!!act.busy} className={outline} data-testid={`doc-primary-${doc.key}`}>Pedir de novo</button>}
      {doc.file_id && <button onClick={() => setViewer(true)} className={outline} data-testid={`doc-view-file-${doc.key}`}><Eye size={15} /> Ver documento</button>}
      <MoreMenu doc={doc} act={act} ro={ro} onHistory={onHistory} notNeeded={!ro && decide && mode === "missing" ? () => setInline("resolve") : null} />
    </div>
  );
}

export function DocCard({ pid, p, doc, reason, onChange, focus, onHistory, Detail, onCollapse }) {
  const { user } = useAuth();
  const ro = user?.role === "direccao";
  const [show, setShow] = useState(false);
  const [inline, setInline] = useState(null);
  const [viewer, setViewer] = useState(false);
  useEffect(() => {
    const h = (e) => { if (e.detail === doc.key) setInline("type"); };
    window.addEventListener("doc-move", h);
    return () => window.removeEventListener("doc-move", h);
  }, [doc.key]);
  const [remind, setRemind] = useState(false);
  const fileRef = useRef(null);
  const act = useDocActions(pid, doc, onChange);
  const [ref, flash] = useFocusFlash([doc.key], focus);
  const owner = ownerOf(doc, p);
  const tone = TONE[reason.tone] || TONE.amber;
  const mode = modeOf(doc, reason);
  const open = () => { setShow(true); if (doc.portal_new) api.post(`/processes/${pid}/documents/${doc.key}/seen`).catch(() => {}); };
  const data = compactData(doc, p);
  const closeInline = (done) => {
    setInline(null);
    if (done) { setShow(false); }
    if (done && inline === "again" && owner.role && !ro) setRemind(true);
  };
  const ctx = { doc, owner, act, setInline, fileRef, setRemind, decide: canDecideOn(user, p), onHistory, ro, show, setViewer,
    toggle: () => (show ? setShow(false) : open()), close: () => setShow(false) };
  return (
    <div ref={ref} className={`scroll-mt-32 md:scroll-mt-20 border-l-4 ${tone.bar} pl-4 pr-1 py-4 border-b border-slate-100 last:border-b-0 transition-colors duration-700 ease-out ${flash ? "bg-amber-50" : "bg-transparent"}`} data-flash={flash ? "on" : "off"} data-mode={mode} data-testid={`docline-${doc.key}`}>
      <div className="flex items-start gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0 flex-1">
          <span className="text-[17px] font-semibold navy-text" data-testid={`doc-title-${doc.key}`}><Glossed text={plainName(doc)} /></span>
          <span className="text-sm text-slate-500" data-testid={`doc-owner-${doc.key}`}>{owner.label}</span>
          {mode !== "ok" && <span className={`text-[13px] font-semibold px-2.5 py-0.5 rounded-md ${tone.pill}`} data-testid={`docstate-${doc.key}`}>{reason.tag}</span>}
          {doc.portal_new && <span className="text-[12px] font-semibold px-2 py-0.5 rounded-md bg-ec-green text-white" data-testid={`docnew-${doc.key}`}>Novo</span>}
        </div>
        {onCollapse && <button onClick={onCollapse} className="text-[15px] text-slate-500 hover:underline shrink-0" data-testid={`doc-collapse-${doc.key}`}>Fechar</button>}
      </div>
      <AlertBox reason={reason} doc={doc} pid={pid} ro={ro} onChange={onChange} onPaused={ctx.toggle} />
      {doc.key === "certidao_permanente" && <CertidaoPanel pid={pid} doc={doc} onChange={onChange} ro={ro} />}
      <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.docx" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) act.upload(f); }} data-testid={`file-${doc.key}`} />
      <Actions ctx={ctx} mode={mode} />
      {act.busy === "upload" && <p className="text-sm text-slate-500 inline-flex items-center gap-1.5 mt-2"><Loader2 size={14} className="animate-spin" /> A ler documento…</p>}
      {inline && !show && inline !== "sign" && <Inline kind={inline} doc={doc} p={p} act={act} close={closeInline} />}
      {data.length > 0 && (
        <div className="text-sm text-slate-500 mt-2.5 flex flex-wrap items-center gap-x-1.5" data-testid={`doc-data-${doc.key}`}>
          {data.map((t, i) => <span key={i}>{i > 0 && "· "}<span className={i === 0 ? "text-slate-700 font-medium" : ""}>{t}</span></span>)}
        </div>
      )}
      {doc.info_note && <p className="text-sm text-slate-500 mt-1.5" data-testid={`doc-info-note-${doc.key}`}>{doc.info_note}</p>}
      <AccessCode doc={doc} demo={!!p?.is_example} />
      {show && (
        <div className="mt-2" data-testid={`doc-review-${doc.key}`}>
          <Detail pid={pid} doc={doc} onChange={onChange} />
          {!ro && <Decisions ctx={ctx} reason={reason} mode={mode} />}
          {inline && inline !== "sign" && <Inline kind={inline} doc={doc} p={p} act={act} close={closeInline} />}
        </div>
      )}
      {inline === "sign" && <SignDateDialog pid={pid} ai={doc.ai || {}} onClose={() => setInline(null)} onSaved={onChange} />}
      {viewer && <DocViewer fileId={doc.file_id} title={plainName(doc)} onClose={() => setViewer(false)} />}
      {remind && <RemindDialog pid={pid} role={owner.role} onClose={() => setRemind(false)} onSent={onChange} />}
    </div>
  );
}
